import { Injectable, inject } from '@angular/core';
import { HttpHeaders, HttpClient, HttpParams, HttpResourceRequest } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TypedResourceRequest } from '../Interfaces/api.interface';

export interface ApiRequestOptions {
  params?: HttpParams | Record<string, string | number | boolean | readonly (string | number | boolean)[]>;
  body?: unknown;
  headers?: HttpHeaders | Record<string, string | string[]>;
  skipLoading?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly _http = inject(HttpClient);
  private _baseUrl = environment.coreApi.host;

  /**
   * Single place that factors HttpResourceRequest for use with httpResource().
   * GET Request Configuration
   */
  get<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this.buildRequest<T>(resource, 'GET', options);
  }

  /**
   * POST Request Configuration
   */
  post<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this.buildRequest<T>(resource, 'POST', options);
  }

  /**
   * PUT Request Configuration
   */
  put<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this.buildRequest<T>(resource, 'PUT', options);
  }

  /**
   * PATCH Request Configuration
   */
  patch<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this.buildRequest<T>(resource, 'PATCH', options);
  }

  /**
   * DELETE Request Configuration
   */
  delete<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this.buildRequest<T>(resource, 'DELETE', options);
  }

  /**
   * Execute a request and return the response body as Observable.
   * Used by facades for mutations that need to react to the result (e.g. exchangeCode).
   */
  run<T>(request: HttpResourceRequest): Observable<T> {
    const method = (request.method ?? 'GET') as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    const url = request.url as string;
    const options = {
      body: request.body,
      params: request.params,
      headers: request.headers as HttpHeaders | Record<string, string | string[]> | undefined,
      responseType: 'json' as const,
    };
    return this._http.request<T>(method, url, options).pipe(map((body) => body as T));
  }

  /**
   * Execute a request without subscribing at the call site (e.g. logout).
   * Use for fire-and-forget mutations when rxMutation is not used.
   */
  runAndForget(request: HttpResourceRequest): void {
    const method = (request.method ?? 'GET') as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    const url = request.url as string;
    const options = {
      body: request.body,
      params: request.params,
      headers: request.headers as HttpHeaders | Record<string, string | string[]> | undefined,
    };
    this._http
      .request(method, url, options)
      .subscribe({ error: () => { } });
  }

  /**
   * Internal helper to build the HttpResourceRequest object required by httpResource()
   */
  private buildRequest<T>(
    resource: string,
    method: HttpResourceRequest['method'],
    options: ApiRequestOptions
  ): TypedResourceRequest<T> {
    let headers: HttpHeaders | Record<string, string | string[]> =
      options.headers ?? new HttpHeaders();
    if (options.skipLoading) {
      headers =
        headers instanceof HttpHeaders
          ? (headers as HttpHeaders).append('skip-loading', 'true')
          : { ...headers, 'skip-loading': 'true' };
    }
    const cleanResource = resource.startsWith('/') ? resource.substring(1) : resource;
    return {
      url: `${this._baseUrl}/${cleanResource}`,
      method,
      params: options.params,
      body: options.body,
      headers,
    } as TypedResourceRequest<T>;
  }
}
